import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [searchParams] = useState(() => new URLSearchParams(window.location.search));
  const inviteToken = searchParams.get('token');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'resident'
  });
  const [loading, setLoading] = useState(false);

  // Redirect to login if no invitation token
  if (!inviteToken) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="w-full max-w-md text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_admin-central-39/artifacts/g46w9dxw_Favicon.png" 
              alt="LivCom" 
              className="h-10 w-10"
            />
            <span className="text-3xl font-heading font-bold text-primary">LivCom</span>
          </div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 mb-3">Acceso por invitacion</h1>
          <p className="text-slate-500 mb-6">
            El acceso a LivCom es por invitacion. Revisa tu email o contacta a tu administrador.
          </p>
          <Link to="/login">
            <Button className="bg-accent hover:bg-accent/90 text-white rounded-full px-8 py-5 font-semibold">
              Ir a iniciar sesion
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await register(formData);
      toast.success('¡Cuenta creada exitosamente!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear cuenta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
               <img 
                src="https://customer-assets.emergentagent.com/job_admin-central-39/artifacts/g46w9dxw_Favicon.png" 
                alt="LivCom" 
                className="h-10 w-10"
              />
               <span className="text-3xl font-heading font-bold text-primary">LivCom</span>
            </div>
            <h1 className="text-2xl font-heading font-semibold text-slate-900 mb-2">
              Crear cuenta
            </h1>
            <p className="text-slate-500">
              Únete y comienza a administrar mejor
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div>
              <Label htmlFor="name" className="text-slate-700 font-medium">Nombre completo</Label>
              <Input
                id="name"
                type="text"
                placeholder="Juan Pérez"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                data-testid="name-input"
                className="mt-1 h-11 bg-slate-50 border-slate-200 focus:bg-white transition-colors"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@ejemplo.com"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                data-testid="email-input"
                className="mt-1 h-11 bg-slate-50 border-slate-200 focus:bg-white transition-colors"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700 font-medium">Contraseña</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                minLength={6}
                data-testid="password-input"
                className="mt-1 h-11 bg-slate-50 border-slate-200 focus:bg-white transition-colors"
              />
            </div>

            <div>
              <Label className="text-slate-700 font-medium">Rol</Label>
              <div
                className="mt-1 h-11 bg-slate-100 border border-slate-200 rounded-md flex items-center px-3 text-slate-500 text-sm cursor-not-allowed"
                data-testid="role-display"
              >
                Residente
              </div>
              <p className="text-xs text-slate-400 mt-1">
                El rol de administrador debe ser asignado por un administrador existente.
              </p>
            </div>

            <Button
              type="submit"
              className="w-full bg-accent hover:bg-accent/90 text-white rounded-full py-6 font-semibold shadow-lg transition-all hover:shadow-xl hover:-translate-y-0.5"
              disabled={loading}
              data-testid="register-submit-button"
            >
              {loading ? 'Creando cuenta...' : 'Crear cuenta'}
            </Button>
          </form>

          <p className="text-center text-text-secondary mt-6">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="text-accent font-medium hover:underline" data-testid="login-link">
              Inicia sesión
            </Link>
          </p>

          <Link to="/" className="block text-center text-text-secondary mt-4 hover:text-primary" data-testid="back-home-link">
            ← Volver al inicio
          </Link>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block flex-1 bg-gradient-to-br from-primary to-primary/90 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-white text-center">
            <h2 className="text-4xl font-heading font-bold mb-4">
              Fuiste invitado a LivCom.
            </h2>
            <p className="text-xl text-white/80 mb-8">
              Completa tu perfil para empezar a administrar tu conjunto.
            </p>
            <div className="space-y-3 text-left max-w-md mx-auto">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">1</div>
                <p className="text-white/90">Gestión multi-edificio</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">2</div>
                <p className="text-white/90">Copiloto IA inteligente</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">3</div>
                <p className="text-white/90">Sin dependencia de WhatsApp</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;